// src/components/PrizeWheel.jsx
// Purely presentational prize wheel for the /spin promo page. Rotation is
// driven by the parent (Spin.js) so the weighted-prize draw stays in one
// place — this component only knows how to point at a given slice index.

import React from 'react';
import { PRIZES } from '../lib/spinPrizes';
import ConfettiBurst from './ConfettiBurst';

export const SEGMENT_ANGLE = 360 / PRIZES.length;

// Kept as constants (rather than inline in the transition string below) so
// Spin.js can drive its tick-sound timing off the exact same curve/duration
// the wheel is visually animating with.
export const SPIN_DURATION_MS = 4600;
export const SPIN_EASING = [0.15, 0.85, 0.32, 1];

// How far out label centers sit, as a % of the disc's width from its
// center — roughly midway between the hub's edge and the rim, so labels
// running along the radius clear both.
const LABEL_RADIUS_PCT = 31;

// Returns a rotation value (always greater than previousRotation, so the
// wheel only ever spins forward) that lands the pointer at the top on the
// center of slice `prizeIndex`, after a few full flourish spins.
export function computeTargetRotation(prizeIndex, previousRotation = 0) {
    const mid = prizeIndex * SEGMENT_ANGLE + SEGMENT_ANGLE / 2;
    const jitter = (Math.random() - 0.5) * (SEGMENT_ANGLE * 0.5);
    const targetMod = ((360 - mid + jitter) % 360 + 360) % 360;
    const currentMod = ((previousRotation % 360) + 360) % 360;

    let delta = targetMod - currentMod;
    if (delta <= 0) delta += 360;

    const FULL_SPINS = 6;
    return previousRotation + FULL_SPINS * 360 + delta;
}

const PrizeWheel = ({ rotation, spinning, onSpinClick, spinDisabled, celebrateKey, celebrating }) => {
    const gradient = `conic-gradient(from 0deg, ${PRIZES.map((_, i) => {
        const start = i * SEGMENT_ANGLE;
        const end = start + SEGMENT_ANGLE;
        const color = i % 2 === 0 ? '#141414' : '#CEAC8F';
        return `${color} ${start}deg ${end}deg`;
    }).join(', ')})`;

    return (
        <div className="spin-wheel-wrap">
            <style>{`
                .spin-wheel-wrap {
                    position: relative;
                    /* 100vw minus the page's and wheel box's side padding,
                       so it fills a phone screen without spilling over. */
                    width: min(400px, calc(100vw - 64px));
                    aspect-ratio: 1 / 1;
                    margin: 0 auto;
                }
                .spin-wheel-glow {
                    position: absolute;
                    inset: -20%;
                    background: radial-gradient(circle, rgba(206,172,143,0.18) 0%, transparent 70%);
                    pointer-events: none;
                }
                .spin-wheel-pointer {
                    position: absolute;
                    top: -12px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 11px solid transparent;
                    border-right: 11px solid transparent;
                    border-top: 18px solid #CEAC8F;
                    z-index: 6;
                    filter: drop-shadow(0 2px 3px rgba(0,0,0,0.5));
                }
                .spin-wheel-disc {
                    position: absolute;
                    inset: 0;
                    border-radius: 50%;
                    border: 4px solid #CEAC8F;
                    box-shadow: 0 0 0 1px rgba(206,172,143,0.35), 0 20px 60px rgba(0,0,0,0.55);
                    overflow: hidden;
                }
                .spin-wheel-label {
                    position: absolute;
                    white-space: nowrap;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.02em;
                    text-transform: uppercase;
                }
                @media (min-width: 420px) {
                    .spin-wheel-label { font-size: 11.5px; }
                }
                .spin-wheel-hub {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    /* Scales with the wheel so small phones keep room for labels. */
                    width: 22%;
                    height: 22%;
                    border-radius: 50%;
                    background: #0a0a0a;
                    border: 3px solid #CEAC8F;
                    color: #CEAC8F;
                    font-family: 'Tenor Sans', serif;
                    letter-spacing: 0.12em;
                    font-size: 13px;
                    cursor: pointer;
                    z-index: 5;
                    transition: transform 0.15s ease, opacity 0.2s ease;
                }
                .spin-wheel-hub:active:not(:disabled) {
                    transform: translate(-50%, -50%) scale(0.94);
                }
                .spin-wheel-hub:disabled {
                    cursor: default;
                    opacity: 0.7;
                }
            `}</style>

            <div className="spin-wheel-glow" />
            <div className="spin-wheel-pointer" />
            <ConfettiBurst fireKey={celebrateKey} />

            <div
                className="spin-wheel-disc"
                style={{
                    background: gradient,
                    transform: `rotate(${rotation}deg)`,
                    transition: spinning ? `transform ${SPIN_DURATION_MS}ms cubic-bezier(${SPIN_EASING.join(', ')})` : 'none',
                }}
            >
                {PRIZES.map((prize, i) => {
                    const mid = i * SEGMENT_ANGLE + SEGMENT_ANGLE / 2;
                    const rad = (mid * Math.PI) / 180;
                    // Positioned by trigonometry, then turned to run along the
                    // slice's radius. With 12 slices there isn't room for
                    // horizontal labels — near the top and bottom they'd overlap
                    // their neighbours. Left-half labels are flipped 180° so none
                    // read upside down.
                    const leftPct = 50 + LABEL_RADIUS_PCT * Math.sin(rad);
                    const topPct = 50 - LABEL_RADIUS_PCT * Math.cos(rad);
                    const textColor = i % 2 === 0 ? '#ffffff' : '#050505';
                    return (
                        <span
                            key={`${prize.id}-${i}`}
                            className="spin-wheel-label"
                            style={{
                                left: `${leftPct}%`,
                                top: `${topPct}%`,
                                transform: `translate(-50%, -50%) rotate(${mid > 180 ? mid + 90 : mid - 90}deg)`,
                                color: textColor,
                            }}
                        >
                            {prize.shortLabel}
                        </span>
                    );
                })}
            </div>

            <button
                type="button"
                className="spin-wheel-hub"
                onClick={onSpinClick}
                disabled={spinDisabled}
                aria-label="Spin the wheel"
            >
                {spinning ? '···' : celebrating ? 'WON!' : 'SPIN'}
            </button>
        </div>
    );
};

export default PrizeWheel;
